import { equal, handleAxiosError } from '@/api/helpers';
import { FiltersType } from '@/api/types';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { getFiltersFromColumns } from '@/client/helpers';
import { EmailType, SystemEmailTypeConfig } from '@/client/system-emails/types';
import { Actions, Subjects } from '@/client/users';
import {
  RedirectPaths,
  RedirectPathsEnum,
  TableNamesEnum,
} from '@/common/constants';
import { DialogContext } from '@/common/context';
import { TranslationFunctionType } from '@/common/types';
import {
  DataTable,
  DataTableActions,
  DataTableColumnsMultiselect,
  DataTableColumnType,
  DataTableFilters,
  DataTableToolbar,
  FilterTypeEnum,
} from '@/components/tables/crud';
import {
  useDeleteSystemEmailTypeConfig,
  useSystemEmailsTypesConfigs,
  useUpdateSystemEmailTypeConfig,
} from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { CustomSwitch, SystemEmailsTabs } from '@/system-settings/components';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { AppSwitch } from '@/ui/switch/AppSwitch';
import { emailTypeOptions, getEmailTypesLabels } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { MenuItem } from 'primereact/menuitem';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DialogType } from '../enums';

const pathItems = (account: Account, t: TranslationFunctionType) => [
  {
    label: account?.name,
    url: !account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('generic.system.emails'),
    className: 'active',
    url: RedirectPaths[RedirectPathsEnum.SYSTEM_EMAILS_TEMPLATES](),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('generic.email.configs'),
    url: RedirectPaths[RedirectPathsEnum.SYSTEM_EMAILS_TYPES_CONFIGS](),
    template: AppBreadCrumbTemplate,
  },
];

export const SystemEmailsTypesConfigsPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = usePermission();
  const account = useAppSelector(selectCurrentAccount);
  const { setDialogData } = useContext(DialogContext);
  const { take, skip, sort, apiFilters, onSort, onFilter, onPage } = useTable({
    tableName: TableNamesEnum.EMAIL_CONFIGS_LIST,
  });

  const [showCustom, setShowCustom] = useState<boolean>(!account?.isSystem);

  const [predefinedTemplatesFilter, setPredefinedTemplatesFilter] =
    useState<FiltersType>([
      equal('account', account?.isSystem ? null : account?.id),
    ]);

  const { systemEmailsTypesConfigs, refetch, isLoading, isFetching } =
    useSystemEmailsTypesConfigs({
      take,
      skip,
      filters: [...apiFilters, ...predefinedTemplatesFilter],
      sort: sort && sort.length > 0 ? [sort.join(',')] : [],
    });

  const updateSystemEmailTypeConfig = useUpdateSystemEmailTypeConfig();
  const deleteSystemEmailTypeConfig = useDeleteSystemEmailTypeConfig();

  const menuItems = (emailTemplate: SystemEmailTypeConfig) => {
    const actions: MenuItem[] = [];

    if (
      can(Actions.UPDATE, Subjects.SYSTEM_SETTINGS) &&
      (account?.isSystem || showCustom)
    ) {
      actions.push(
        ...[
          {
            label: t('generic.edit'),
            icon: 'pi pi-pencil',
            command: () =>
              navigate(
                RedirectPaths[
                  RedirectPathsEnum.SYSTEM_EMAILS_TYPES_CONFIGS_EDIT
                ](emailTemplate?.id),
              ),
          },
          {
            label: emailTemplate.active
              ? t('generic.deactivate')
              : t('generic.activate'),
            icon: emailTemplate.active ? 'pi pi-minus-circle' : 'pi pi-check',
            command: () =>
              setDialogData({
                type: DialogType.CONFIRMATION,
                show: true,
                header: emailTemplate.active
                  ? t('dialog.deactivate.confirm')
                  : t('dialog.activate.confirm'),
                message: emailTemplate.active
                  ? t('system.email.type.config.deactivate.success')
                  : t('system.email.type.config.activate.success'),
                onAccept: async () =>
                  await handleToggleActiveStatus(
                    emailTemplate.id,
                    emailTemplate.active as boolean,
                  ),
              }),
          },
        ],
      );
    }

    if (
      can(Actions.DELETE, Subjects.SYSTEM_SETTINGS) &&
      (account?.isSystem || showCustom)
    ) {
      actions.push({
        label: t('generic.delete'),
        icon: 'pi pi-times',
        command: () =>
          setDialogData({
            type: DialogType.CONFIRMATION,
            show: true,
            header: t('dialog.delete.confirm'),
            message: account?.isSystem
              ? t('system.email.type.config.delete.predefined')
              : t('system.email.type.config.delete.custom'),
            onAccept: async () =>
              await handleDeleteEmailTypeConfig(emailTemplate.id),
          }),
      });
    }

    return actions;
  };

  const columns: DataTableColumnType[] = [
    {
      field: 'type',
      header: t('system.email.templates.emailType'),
      sortable: true,
      filterable: true,
      filters: {
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('generic.type.search'),
        options: emailTypeOptions(t),
      },
      render: (row: SystemEmailTypeConfig) =>
        getEmailTypesLabels(t)[row.type as EmailType],
    },
    {
      field: 'active',
      header: t('generic.active'),
      sortable: true,
      filterable: true,
      filters: { type: FilterTypeEnum.SELECT_ACTIVE_INACTIVE },
      render: (row: SystemEmailTypeConfig) => (
        <AppSwitch
          disabled={!showCustom && !account?.isSystem}
          checked={!!row.active}
          onChange={() =>
            setDialogData({
              type: DialogType.CONFIRMATION,
              show: true,
              header: row.active
                ? t('dialog.deactivate.confirm')
                : t('dialog.activate.confirm'),
              message: row.active
                ? t('system.email.type.config.deactivate')
                : t('system.email.type.config.activate'),
              onAccept: async () => await handleToggleActiveConfig(row),
            })
          }
        />
      ),
    },
    {
      field: 'actions',
      header: t('generic.actions'),
      sortable: false,
      filterable: false,
      style: {
        width: '80px',
        textAlign: 'center' as const,
      },
      render: (row: SystemEmailTypeConfig) => (
        <DataTableActions
          disabled={menuItems(row).length < 1}
          menuItems={menuItems(row)}
        />
      ),
    },
  ];

  // Set the preselected columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const defaultVisibleColumns = columns.map((column) => column.field);
  const alwaysVisibleColumns = ['type', 'actions'];
  //

  const handleToggleActiveConfig = async (data: SystemEmailTypeConfig) => {
    try {
      await updateSystemEmailTypeConfig.update({
        configId: data.id,
        active: !data.active,
      });
      await refetch();
      toast?.success(
        t('toast.success'),
        data.active
          ? t('system.email.type.config.deactivate.success')
          : t('system.email.type.config.activate.success'),
      );
    } catch (e) {
      handleAxiosError(e as AxiosError, toast);
    }
  };

  const handleToggleActiveStatus = async (
    emailTemplateId: string,
    active: boolean,
  ) => {
    try {
      await updateSystemEmailTypeConfig.update({
        configId: emailTemplateId,
        active: !active,
      });
      await refetch();
      toast?.success(
        t('toast.success'),
        active
          ? t('system.email.type.config.deactivate.success')
          : t('system.email.type.config.activate.success'),
      );
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleDeleteEmailTypeConfig = async (emailTemplateId: string) => {
    try {
      await deleteSystemEmailTypeConfig.delete(emailTemplateId);
      setTimeout(() => {
        refetch();
      }, 100);
      toast?.success(
        t('toast.success'),
        account?.isSystem
          ? t('system.email.type.config.delete.predefined.success')
          : t('system.email.type.config.delete.custom.success'),
      );
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleToggle = () => {
    setShowCustom((state) => !state);
    setPredefinedTemplatesFilter([
      equal('account', showCustom ? null : account?.id),
    ]);
  };

  const toolbar = (
    <DataTableToolbar>
      <FlexContainer
        justify="space-between"
        gap={8}
        align="flex-start"
        wrap="wrap"
      >
        <DataTableFilters
          filters={getFiltersFromColumns(columns)}
          onFilter={onFilter}
          tableName={TableNamesEnum.EMAIL_CONFIGS_LIST}
        />
        {can(Actions.CREATE, Subjects.SYSTEM_SETTINGS) &&
          (account?.isSystem || showCustom) && (
            <AppButton
              label={t('button.createNew')}
              severity="secondary"
              onClick={() => {
                navigate(
                  RedirectPaths[
                    RedirectPathsEnum.SYSTEM_EMAILS_TYPES_CONFIGS_CREATE
                  ](),
                );
              }}
            />
          )}
      </FlexContainer>
      <DataTableColumnsMultiselect
        columns={columns}
        tableName={TableNamesEnum.EMAIL_CONFIGS_LIST}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        alwaysVisibleColumns={alwaysVisibleColumns}
      />
    </DataTableToolbar>
  );

  return (
    <>
      <AppBreadCrumb model={pathItems(account as Account, t)} />
      <FlexContainer justify="space-between" className="mb-4">
        <h1 className="mb-0">{t('generic.email.configs')}</h1>
      </FlexContainer>
      <SystemEmailsTabs className="mb-4" />
      {!account?.isSystem && (
        <CustomSwitch
          checked={!!showCustom}
          label={t('campaign.testEmail.custom')}
          onChange={handleToggle}
        />
      )}
      <DataTable
        data={systemEmailsTypesConfigs?.result}
        count={systemEmailsTypesConfigs?.count}
        isLoading={isLoading}
        isFetching={isFetching}
        toolbar={toolbar}
        columns={columns}
        visibleColumns={visibleColumns}
        rows={take}
        skip={skip}
        onPage={onPage}
        onSort={onSort}
        sort={sort}
      />
    </>
  );
};
